import {
	ComIbmCommerceRestMemberHandlerGuestIdentityHandlerGuestIdentityForm,
	ComIbmCommerceRestMemberHandlerGuestIdentityHandlerUserIdentity,
} from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class GuestIdentity<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Creates identity tokens for a guest user.
	 *
	 * @tags Guest Identity
	 * @name GuestIdentityLogin
	 * @summary Create identity token
	 * @request POST:/store/{storeId}/guestidentity
	 * @secure
	 * @response `200` `ComIbmCommerceRestMemberHandlerGuestIdentityHandlerUserIdentity` The requested completed successfully.
	 * @response `201` `void` The requested resource has been created.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	guestIdentityLogin = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		data?: ComIbmCommerceRestMemberHandlerGuestIdentityHandlerGuestIdentityForm,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['guestIdentityLogin'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/guestidentity`,
				params: _params,
				query: query,
				body: data,
				methodName: 'guestIdentityLogin',
			};
			logger.trace(traceData);
		}
		return this.http.request<ComIbmCommerceRestMemberHandlerGuestIdentityHandlerUserIdentity, void>(
			{
				path: `/store/${storeId}/guestidentity`,
				method: 'POST',
				query: query,
				body: data,
				secure: true,
				type: params.type ?? ContentType.Json,
				format: params.format ?? 'json',
				storeId,
				traceData,
				skipErrorLogging,
				..._params,
			}
		);
	};
	/**
	 * @description Logs out a guest user.
	 *
	 * @tags Guest Identity
	 * @name GuestIdentityLogout
	 * @summary Log off guest user
	 * @request DELETE:/store/{storeId}/guestidentity/@self
	 * @secure
	 * @response `200` `void` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	guestIdentityLogout = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['guestIdentityLogout'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/guestidentity/@self`,
				params: _params,
				query: query,
				methodName: 'guestIdentityLogout',
			};
			logger.trace(traceData);
		}
		return this.http.request<void, void>({
			path: `/store/${storeId}/guestidentity/@self`,
			method: 'DELETE',
			query: query,
			secure: true,
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
